import { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const FormContext = createContext()

const STORAGE_KEY = 'dlr_form_v1'

// Complete form data structure based on project requirements
const defaultFormData = {
  // Step 1: Site Base
  siteBase: '', // 'basic' or 'ecommerce'

  // Step 2: Design
  designApproach: '', // 'template' or 'custom'
  selectedTemplate: '', // template ID if template approach

  // Step 3: Customization
  logoOption: '', // 'have_logo', 'need_development', 'provide_later'
  logoFile: null, // Will be { name, size, url, uploadedAt, storagePath, type }
  brandColors: {
    primary: '#000000',
    secondary: '#000000',
    accent: '#000000',
    useTemplateDefaults: false
  },
  designNotes: '',

  // Step 3b: Custom Design (if custom approach)
  customDesign: {
    businessDescription: '',
    stylePreferences: [],
    otherStyles: '',
    colorPreference: '', // 'have_colors', 'need_recommendations', 'industry_based'
    colorMood: '',
    inspirationSites: [],
    logoStylePreferences: [],
    logoNotes: '',
    existingBrandMaterials: [],
    brandMaterialFiles: [], // Files uploaded for brand materials
    feedbackMethod: ''
  },

  // Step 4: Pages
  pages: {
    default: [
      { id: 'home', name: 'Home Page', type: 'home', icon: '🏠', desc: 'Your main landing page', locked: true, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 },
      { id: 'about', name: 'About / Company', type: 'about', icon: '📖', desc: 'Tell your story', locked: false, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 },
      { id: 'contact', name: 'Contact Page', type: 'contact', icon: '📧', desc: 'Get in touch form', locked: false, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 },
      { id: 'transactions', name: 'Commercial Transactions Act', type: 'legal', icon: '⚖️', desc: 'Legal compliance page', locked: false, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 }
    ],
    additional: [] // {id, type, name, complexity, specifications, files, cost, timelineDays}
  },

  // Step 5: Add-ons
  addons: {
    domain: {
      option: '', // 'register_new', 'existing', 'decide_later'
      newDomainName: '',
      newDomainExtension: '.jp',
      existingDomain: ''
    },
    managedSupport: {
      selected: false,
      startMonth: 4 // Month 1 or Month 4
    },
    corporateEmail: {
      selected: false,
      users: 1
    },
    websiteChat: {
      selected: false,
      users: 1,
      monthlyCostPerUser: 3900 // ¥3,900/month per user (~$25 USD)
    },
    appointmentScheduler: {
      selected: false,
      users: 1,
      monthlyCostPerUser: 1600 // ¥1,600/month per user (~$10 USD)
    },
    businessCards: {
      selected: false,
      deliveryAddress: ''
    }
  },

  // Step 6: Additional Requests
  additionalRequests: {
    customDevelopment: '',
    documents: [],
    timelinePreference: '', // 'asap', 'specific_date', 'flexible'
    specificDate: '',
    isDeadlineCritical: false,
    additionalNotes: ''
  },

  // Step 7: Contact & Submit
  contact: {
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    preferredLanguage: 'english', // 'japanese', 'english', 'both'
    bestContactTimes: [] // ['morning', 'afternoon', 'evening']
  },

  agreements: {
    understandsPricing: false,
    agreesToProvideContent: false,
    agreesToTerms: false
  },

  // Summary/Calculated fields
  costs: {
    setupTotal: 0,
    setupBreakdown: {},
    monthlyRecurring: 0,
    monthlyBreakdown: {}
  },

  timeline: {
    estimatedWeeks: 2,
    estimatedDays: 0
  }
}

// Helper to get default pages based on site type
const getDefaultPages = (siteBase) => {
  const basicPages = [
    { id: 'home', name: 'Home Page', type: 'home', icon: '🏠', desc: 'Your main landing page', locked: true, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 },
    { id: 'about', name: 'About / Company', type: 'about', icon: '📖', desc: 'Tell your story', locked: false, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 },
    { id: 'contact', name: 'Contact Page', type: 'contact', icon: '📧', desc: 'Get in touch form', locked: false, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 },
    { id: 'transactions', name: 'Commercial Transactions Act', type: 'legal', icon: '⚖️', desc: 'Legal compliance page', locked: false, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 }
  ]

  if (siteBase === 'ecommerce') {
    return [
      ...basicPages.slice(0, 2), // Home and About
      { id: 'shop', name: 'Shop / Products', type: 'shop', icon: '🛍️', desc: 'Product catalog', locked: false, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 },
      ...basicPages.slice(2), // Contact and Legal
      { id: 'blog', name: 'Blog Page', type: 'blog', icon: '📝', desc: '5 starter posts included', locked: false, complexity: '', specifications: '', files: [], configured: false, cost: 0, timelineDays: 0 }
    ]
  }

  return basicPages
}

export function FormProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true) // Add loading state
  const [formData, setFormData] = useState(defaultFormData)
  const [currentStep, setCurrentStep] = useState(1)
  const [navigate, setNavigate] = useState(null)
  const [lastSaved, setLastSaved] = useState(null)
  const [showResumeModal, setShowResumeModal] = useState(false)
  const [toast, setToast] = useState(null)
  const [isUploading, setIsUploading] = useState(false) // Track upload state
  const [editMode, setEditMode] = useState(false) // Track if editing existing request
  const [editingRequestId, setEditingRequestId] = useState(null) // Request ID being edited

  // Load from localStorage on mount - SYNCHRONOUSLY before anything else
  useEffect(() => {
    // Don't show resume modal if on thank-you or edit pages
    const currentPath = window.location.pathname
    const isSpecialPage = currentPath.startsWith('/thank-you') || currentPath.startsWith('/edit')

    const savedData = localStorage.getItem(STORAGE_KEY)
    if (savedData && !isSpecialPage) {
      try {
        const parsed = JSON.parse(savedData)
        const savedDate = new Date(parsed.lastSaved)
        const now = new Date()
        const daysDiff = (now - savedDate) / (1000 * 60 * 60 * 24)

        // Check if data is expired (7 days)
        if (daysDiff > 7) {
          console.log('Saved data expired (>7 days), clearing...')
          localStorage.removeItem(STORAGE_KEY)
          setIsLoading(false)
          return
        }

        // Load saved data IMMEDIATELY
        console.log('Loading saved data, step:', parsed.currentStep)
        setFormData(parsed.formData || defaultFormData)
        setCurrentStep(parsed.currentStep || 1)
        setLastSaved(parsed.lastSaved)
        setEditMode(parsed.editMode || false) // Restore edit mode
        setEditingRequestId(parsed.editingRequestId || null) // Restore editing request ID
        setShowResumeModal(true)
      } catch (error) {
        console.error('Error loading saved form data:', error)
        localStorage.removeItem(STORAGE_KEY)
      }
    }
    setIsLoading(false) // Mark as loaded
  }, [])

  // Save to localStorage whenever formData or currentStep changes
  // Only save if user has progressed past Step 1 (currentStep > 1)
  useEffect(() => {
    // Only start saving once they've moved to Step 2 or beyond
    if (currentStep > 1) {
      const now = new Date().toISOString()
      const dataToSave = {
        lastSaved: now,
        currentStep,
        formData,
        editMode, // Save edit mode state
        editingRequestId // Save request ID being edited
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
      setLastSaved(now)
    }
  }, [formData, currentStep, editMode, editingRequestId])

  const updateFormData = (updates) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        ...updates
      }

      // If siteBase changes, update default pages
      if (updates.siteBase && updates.siteBase !== prev.siteBase) {
        newData.pages = {
          ...prev.pages,
          default: getDefaultPages(updates.siteBase)
        }
      }

      return newData
    })
  }

  const updateNestedFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev }
      const keys = path.split('.')
      let current = newData

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] }
        current = current[keys[i]]
      }

      current[keys[keys.length - 1]] = value
      return newData
    })
  }

  const clearFormData = (skipNavigation = false) => {
    localStorage.removeItem(STORAGE_KEY)
    setFormData(defaultFormData)
    setCurrentStep(1)
    setLastSaved(null)
    setEditMode(false)
    setEditingRequestId(null)

    if (!skipNavigation) {
      showToast('Progress cleared. Starting fresh!', 'info')
      if (navigate) {
        navigate('/step/1')
      }
    }
  }

  const manualSave = () => {
    const now = new Date().toISOString()
    const dataToSave = {
      lastSaved: now,
      currentStep,
      formData
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
    setLastSaved(now)
    showToast('Progress saved to your browser!', 'success')
  }

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
  }

  const hideToast = () => {
    setToast(null)
  }

  const handleResumeProgress = () => {
    const savedStep = currentStep
    setShowResumeModal(false)

    // Navigate to the saved step immediately
    if (navigate && savedStep >= 1) {
      navigate(`/step/${savedStep}`, { replace: true })
    }

    // Show toast after navigation
    setTimeout(() => {
      showToast('Continuing from where you left off', 'success')
    }, 200)
  }

  const handleStartFresh = () => {
    localStorage.removeItem(STORAGE_KEY)
    setFormData(defaultFormData)
    setCurrentStep(1)
    setLastSaved(null)
    setShowResumeModal(false)
    showToast('Starting fresh!', 'info')
    if (navigate) {
      navigate('/step/1')
    }
  }

  const goToNextStep = () => {
    if (currentStep < 7) {
      const nextStep = currentStep + 1

      // Save immediately before navigating (don't wait for useEffect)
      const now = new Date().toISOString()
      const dataToSave = {
        lastSaved: now,
        currentStep: nextStep, // Save the NEXT step
        formData
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
      setLastSaved(now)

      // Then update state and navigate
      setCurrentStep(nextStep)
      if (navigate) {
        navigate(`/step/${nextStep}`)
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1
      setCurrentStep(prevStep)
      if (navigate) {
        navigate(`/step/${prevStep}`)
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const value = {
    formData,
    setFormData,
    updateFormData,
    updateNestedFormData,
    currentStep,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    clearFormData,
    manualSave,
    setNavigate,
    lastSaved,
    showResumeModal,
    handleResumeProgress,
    handleStartFresh,
    toast,
    showToast,
    hideToast,
    isLoading,
    isUploading,
    setIsUploading,
    editMode,
    setEditMode,
    editingRequestId,
    setEditingRequestId
  }

  return (
    <FormContext.Provider value={value}>
      {children}
    </FormContext.Provider>
  )
}

export function useForm() {
  const context = useContext(FormContext)
  if (!context) {
    throw new Error('useForm must be used within FormProvider')
  }
  return context
}
